import React, { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("orders");

  const { data: orders = [], isLoading: ordersL } = useQuery({
    queryKey: ["orders", "mine"],
    queryFn: async () => (await api.get("/orders/mine")).data,
    enabled: !!user,
  });
  const { data: wishlist = [] } = useQuery({
    queryKey: ["wishlist"], queryFn: async () => (await api.get("/wishlist")).data, enabled: !!user,
  });
  const { data: addresses = [] } = useQuery({
    queryKey: ["addresses"], queryFn: async () => (await api.get("/me/addresses")).data, enabled: !!user,
  });

  const removeWl = useMutation({
    mutationFn: async (pid) => (await api.delete(`/wishlist/${pid}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wishlist"] }),
  });

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-20 text-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="bg-white" data-testid="account-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="rh-overline mb-3">Account</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-2">Hello, {user.name?.split(" ")[0] || "friend"}.</h1>
        <p className="text-[#4a4a4a] mb-10">{user.email}</p>

        <div className="border-b border-[#0a0a0a] flex gap-1 mb-8">
          {[["orders", "Orders"], ["wishlist", `Wishlist (${wishlist.length})`], ["addresses", "Addresses"]].map(([k, label]) => (
            <button key={k} data-testid={`acct-tab-${k}`} onClick={() => setTab(k)}
                    className={`px-5 py-3 text-sm font-semibold uppercase tracking-widest -mb-px border-b-2 ${tab === k ? "border-[#002fa7] text-[#002fa7]" : "border-transparent text-[#4a4a4a]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          ordersL ? <div className="text-[#4a4a4a]">Loading…</div> :
          orders.length === 0 ? <div className="border border-[#0a0a0a] py-12 text-center text-[#4a4a4a]">No orders yet.</div> :
          <div className="border-t border-[#0a0a0a]">
            {orders.map((o) => (
              <div key={o.order_id} className="grid grid-cols-12 gap-4 py-5 border-b border-[#e5e5e5] items-center" data-testid={`order-${o.order_id}`}>
                <div className="col-span-12 md:col-span-4"><div className="rh-overline">Order</div><div className="font-mono font-semibold mt-1">{o.order_id}</div><div className="text-xs text-[#4a4a4a]">{new Date(o.created_at).toLocaleDateString()}</div></div>
                <div className="col-span-6 md:col-span-3"><div className="rh-overline">Items</div><div className="text-sm mt-1">{o.items.length} item{o.items.length !== 1 ? "s" : ""}</div></div>
                <div className="col-span-6 md:col-span-3"><div className="rh-overline">Status</div><span className="inline-block mt-1 bg-[#0a0a0a] text-white px-2 py-1 text-[10px] uppercase tracking-widest font-semibold">{o.status}</span></div>
                <div className="col-span-12 md:col-span-2 md:text-right"><div className="rh-overline">Total</div><div className="font-display font-bold rh-num text-xl mt-1">${o.total.toFixed(2)}</div></div>
              </div>
            ))}
          </div>
        )}

        {tab === "wishlist" && (
          wishlist.length === 0 ? <div className="border border-[#0a0a0a] py-12 text-center text-[#4a4a4a]">No saved items yet. Tap the <Heart size={14} className="inline" /> on any product.</div> :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {wishlist.map((p) => (
              <div key={p.product_id} className="border border-[#e5e5e5] p-4 flex gap-3" data-testid={`wl-item-${p.product_id}`}>
                <Link to={`/product/${p.product_id}`} className="w-20 h-20 bg-[#f5f5f5]"><img src={p.image} alt="" className="w-full h-full object-cover" /></Link>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono uppercase text-[#4a4a4a]">{p.brand}</div>
                  <Link to={`/product/${p.product_id}`} className="text-sm font-semibold line-clamp-2">{p.name}</Link>
                  <div className="font-display font-bold rh-num mt-1">${p.price.toFixed(2)}</div>
                </div>
                <button onClick={() => removeWl.mutate(p.product_id)} className="text-[#4a4a4a] hover:text-[#ff3b30]"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "addresses" && <AddressesTab addresses={addresses} qc={qc} />}
      </div>
    </div>
  );
}

function AddressesTab({ addresses, qc }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ label: "Home", full_name: "", address_line1: "", address_line2: "", city: "", state: "", postal_code: "", country: "United States", phone: "" });

  const save = useMutation({
    mutationFn: async () => (await api.post("/me/addresses", form)).data,
    onSuccess: () => { toast.success("Address saved"); setShow(false); qc.invalidateQueries({ queryKey: ["addresses"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/me/addresses/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["addresses"] }),
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div className="text-sm text-[#4a4a4a]">{addresses.length} saved</div>
        <button onClick={() => setShow(true)} className="rh-btn-primary inline-flex items-center gap-2" data-testid="add-address-btn"><Plus size={14} /> New address</button>
      </div>
      {addresses.length === 0 ? <div className="border border-[#0a0a0a] py-12 text-center text-[#4a4a4a]">No saved addresses yet.</div> :
        <div className="grid sm:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <div key={a.address_id} className="border border-[#e5e5e5] p-5 text-sm" data-testid={`addr-${a.address_id}`}>
              <div className="flex justify-between"><div className="rh-overline">{a.label}</div><button onClick={() => del.mutate(a.address_id)} className="text-[#4a4a4a] hover:text-[#ff3b30]"><Trash2 size={14} /></button></div>
              <div className="mt-2 font-semibold">{a.full_name}</div>
              <div className="text-[#4a4a4a]">{a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ""}<br />{a.city}, {a.state} {a.postal_code}<br />{a.country}<br />{a.phone}</div>
            </div>
          ))}
        </div>
      }
      {show && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
                className="bg-white border border-[#0a0a0a] w-full max-w-lg p-6 grid sm:grid-cols-2 gap-3">
            <h3 className="font-display font-bold text-xl sm:col-span-2">New address</h3>
            {[["label", "Label"], ["full_name", "Full name"], ["address_line1", "Address line 1", "sm:col-span-2"], ["address_line2", "Address line 2", "sm:col-span-2"], ["city", "City"], ["state", "State"], ["postal_code", "Postal code"], ["country", "Country"], ["phone", "Phone"]].map(([k, lbl, cls]) => (
              <label key={k} className={`block ${cls || ""}`}>
                <span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-1">{lbl}</span>
                <input data-testid={`addr-field-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={k !== "address_line2"} className="w-full border border-[#0a0a0a] px-3 py-2 text-sm" />
              </label>
            ))}
            <div className="sm:col-span-2 flex gap-3 justify-end pt-2">
              <button type="button" onClick={() => setShow(false)} className="rh-btn-secondary">Cancel</button>
              <button type="submit" className="rh-btn-primary" data-testid="save-address-btn">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
